import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import publicMapService from '../../services/publicMapService';
import {
  addOsmTileLayer,
  createRentahabPinIcon,
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM,
} from '../maps/openStreetMapUtils';

const formatLocation = (building) =>
  [building.city, building.province, building.country].filter(Boolean).join(', ');

const ClientsMap = ({ embedded = false, onStatsChange, className = '' }) => {
  const onStatsChangeRef = useRef(onStatsChange);
  onStatsChangeRef.current = onStatsChange;
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersLayerRef = useRef(null);
  const [stats, setStats] = useState({
    buildings: 0,
    clients: 0,
    cities: 0,
    loading: true,
    error: false,
  });

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: DEFAULT_MAP_CENTER,
      zoom: DEFAULT_MAP_ZOOM,
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: false,
      dragging: true,
    });

    addOsmTileLayer(map);

    L.control
      .attribution({ position: 'bottomright', prefix: false })
      .addAttribution('© OpenStreetMap')
      .addTo(map);

    markersLayerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      markersLayerRef.current = null;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadBuildings = async () => {
      try {
        const data = await publicMapService.fetchClientsMap();
        if (cancelled || !mapRef.current || !markersLayerRef.current) return;

        markersLayerRef.current.clearLayers();
        const buildings = data.buildings || [];
        const markerIcon = createRentahabPinIcon();
        const bounds = [];

        buildings.forEach((building) => {
          const lat = building.latitude;
          const lng = building.longitude;
          if (lat == null || lng == null) return;

          const marker = L.marker([lat, lng], { icon: markerIcon });
          const location = formatLocation(building);

          marker.bindPopup(
            `<div style="font-family:Inter,system-ui,sans-serif;min-width:160px">
              <strong style="color:#2E37A4;font-size:13px">${building.inmueble_nombre}</strong>
              <div style="color:#475569;font-size:12px;margin-top:2px">${building.company_name}</div>
              ${location ? `<div style="color:#64748b;font-size:11px;margin-top:4px">${location}</div>` : ''}
            </div>`
          );
          marker.addTo(markersLayerRef.current);
          bounds.push([lat, lng]);
        });

        if (bounds.length === 1) {
          mapRef.current.setView(bounds[0], 11);
        } else if (bounds.length > 1) {
          mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
        }

        setStats({
          buildings: data.total_buildings ?? buildings.length,
          clients: data.total_clients ?? 0,
          cities: data.cities ?? 0,
          loading: false,
          error: false,
        });
        onStatsChangeRef.current?.({
          buildings: data.total_buildings ?? buildings.length,
          clients: data.total_clients ?? 0,
          cities: data.cities ?? 0,
        });
      } catch {
        if (!cancelled) {
          setStats((prev) => ({ ...prev, loading: false, error: true }));
        }
      }
    };

    loadBuildings();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className={`flex flex-col flex-1 min-h-0 ${className}`}>
      <div
        className={`relative flex-1 min-h-[220px] overflow-hidden ${
          embedded ? 'rounded-2xl border border-white/20 shadow-2xl' : 'rounded-2xl border border-white/10 shadow-glow'
        }`}
      >
        <div ref={mapContainerRef} className="absolute inset-0 z-0 clients-map-tiles" />
        {!embedded && (
          <div className="absolute inset-0 z-[1] pointer-events-none bg-gradient-to-t from-rentahab-900/80 via-rentahab-900/20 to-rentahab-800/30" />
        )}
        {embedded && (
          <div className="absolute inset-0 z-[1] pointer-events-none bg-gradient-to-br from-rentahab-900/10 to-transparent" />
        )}

        {stats.loading && (
          <div className="absolute inset-0 z-[2] flex items-center justify-center bg-rentahab-900/40 backdrop-blur-sm">
            <div className="flex items-center gap-2 text-white/80 text-sm">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Cargando edificios...
            </div>
          </div>
        )}

        {!stats.loading && stats.buildings > 0 && !embedded && (
          <div className="absolute bottom-3 left-3 z-[2] flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur-md border border-white/20 px-3 py-1 text-xs font-medium text-white">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              {stats.buildings} {stats.buildings === 1 ? 'edificio' : 'edificios'}
            </span>
            {stats.clients > 0 && (
              <span className="inline-flex items-center rounded-full bg-white/10 backdrop-blur-md border border-white/15 px-3 py-1 text-xs text-rentahab-100">
                {stats.clients} {stats.clients === 1 ? 'cliente' : 'clientes'}
              </span>
            )}
            {stats.cities > 0 && (
              <span className="inline-flex items-center rounded-full bg-white/10 backdrop-blur-md border border-white/15 px-3 py-1 text-xs text-rentahab-100">
                {stats.cities} {stats.cities === 1 ? 'ciudad' : 'ciudades'}
              </span>
            )}
          </div>
        )}

        {!stats.loading && stats.error && (
          <div className="absolute inset-0 z-[2] flex items-center justify-center">
            <p className="text-rentahab-200/70 text-sm">Mapa no disponible</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientsMap;
